import { saveAs } from "file-saver";
import { concat, merge, range, snakeCase } from "lodash";
import { PlotData } from "plotly.js";
import { createObjectCsvStringifier } from "csv-writer-browser";
import {
  I2dFigure,
  I2dStructure,
  I3dStructure,
  I3dSurface,
  IBarChart,
  IOutput,
  IPieChart,
  ITimeChart,
  IWaterfallChart,
} from "../types";
import { logError } from "utils/utils";

const download = (value: string, mimetype: string, filename: string) => {
  const file = merge(
    new Blob([value ? value : ""], {
      type: mimetype,
    }),
    {
      lastModifiedDate: new Date(),
      name: filename,
    }
  );
  saveAs(file, file.name);
};

/**
 * Download the chart data as a CSV file.
 *
 * @param output
 * @param data
 */
export const downloadData = (output: IOutput, data: Array<Partial<PlotData>>) => {
  const header = [{ id: "f", title: "SERIES" }];
  const figures = [] as Array<{ figure: string; data?: string }>;
  let transform: (figure: { figure: string; data?: string }, data: Partial<PlotData>) => Array<any>;
  switch (output.type) {
    case "3d_surface": {
      const chart = output as I3dSurface;
      header.push({ id: "x", title: "X_VALUE" });
      header.push({ id: "y", title: "Y_VALUE" });
      header.push({ id: "z", title: "Z_VALUE" });
      chart.figures.forEach((f, i) => figures.push({ figure: f.label || (f as I3dStructure).label || `${i}` }));
      transform = (f, d) =>
        range(0, Math.min(d.x?.length || 0, d.y?.length || 0, d.z?.length || 0)).map((i) => ({
          f: f.figure,
          x: d.x?.[i],
          y: d.y?.[i],
          z: d.z?.[i],
        }));
      break;
    }
    case "bar_chart": {
      const chart = output as IBarChart;
      header.push({ id: "x", title: "X_VALUE" });
      header.push({ id: "y", title: "Y_VALUE" });
      chart.figures.forEach((f, i) =>
        figures.push({ figure: f.label || (f as I2dFigure).y_data?.label || (f as I2dStructure).label || `${i}` })
      );
      transform = (f, d) =>
        range(0, Math.min(d.x?.length || 0, d.y?.length || 0)).map((i) => ({
          f: f.figure,
          x: d.x?.[i],
          y: d.y?.[i],
        }));
      break;
    }
    case "waterfall_chart": {
      const chart = output as IWaterfallChart;
      header.push({ id: "m", title: "M_VALUE" });
      header.push({ id: "x", title: "X_VALUE" });
      header.push({ id: "y", title: "Y_VALUE" });
      chart.figures.forEach((f, i) =>
        figures.push({ figure: f.label || (f as I2dFigure).y_data?.label || (f as I2dStructure).label || `${i}` })
      );
      transform = (f, d) =>
        range(0, Math.min((d as any).measure?.length || d.x?.length || 0, d.y?.length || 0)).map((i) => ({
          f: f.figure,
          m: (d as any).measure?.[i],
          x: d.x?.[i],
          y: d.y?.[i],
        }));
      break;
    }
    case "pie_chart": {
      const chart = output as IPieChart;
      header.push({ id: "l", title: "LABEL" });
      header.push({ id: "v", title: "VALUE" });
      chart.figures.forEach((f, i) => figures.push({ figure: f.label || `${i}` }));
      transform = (f, d) =>
        range(0, Math.min(d.labels?.length || 0, d.values?.length || 0)).map((i) => ({
          f: f.figure,
          l: d.labels?.[i],
          v: d.values?.[i],
        }));
      break;
    }
    case "time_chart": {
      const chart = output as ITimeChart;
      header.push({ id: "d", title: "LABEL" });
      header.push({ id: "x", title: "X_VALUE" });
      header.push({ id: "y", title: "Y_VALUE" });
      chart.figures.forEach((f, i) =>
        f.data.forEach((d, j) => figures.push({ figure: f.label || `${i}`, data: d.label || `${j}` }))
      );
      transform = (f, d) =>
        range(0, Math.min(d.x?.length || 0, d.y?.length || 0)).map((i) => ({
          f: f.figure,
          d: f.data,
          x: d.x?.[i],
          y: d.y?.[i],
        }));
      break;
    }
    default:
      logError(new Error(`Unknown output type: ${output.type}`));
  }
  const csvStringifier = createObjectCsvStringifier({ header });
  const records = concat(...data.map((f, i) => transform(figures[i], f)));
  const result = csvStringifier.getHeaderString() + csvStringifier.stringifyRecords(records);
  download(result, "application/csv", `${snakeCase(output.label || output.name || "unknown")}-${output.type}.csv`);
};
